/**
 * DocumentTemplateGallery - browse the REAL document templates,
 * upload a document to turn into your own editable design, and
 * reopen the document designs you have saved.
 *
 * 2026-11-05 honesty pass (audit: document-editor-audit-2026-11-05.md):
 * - The old catalogue was fabricated: loop-generated entries with
 *   Math.random() download counts and ratings and "✓ OFFICIAL"
 *   badges. It now lists exactly the templates the editor really
 *   has (utils/globalDocTemplates) — every count is the real one.
 * - Card clicks were dead (App passes no onSelectTemplate). Cards
 *   now open /document-editor with that template loaded.
 * - Upload discarded the file while alerting success. It now runs
 *   the same clone flow as the editor and persists the result as a
 *   saved design — account (kind 'globaldoc') when signed in, this
 *   browser otherwise — and reports where it actually landed.
 * - My Templates was a static empty state. It now lists the
 *   designs saved in this browser and in the account, each openable
 *   in the editor.
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { UniversalDocumentCloningEngine } from '../engines/UniversalDocumentCloningEngine';
import {
    GLOBAL_DOC_TEMPLATE_LIST, GLOBAL_DOC_CATEGORIES, mapClonedDocument, DEFAULT_DOC_BORDER,
    GlobalDocTemplateMeta
} from '../utils/globalDocTemplates';
import {
    readLocalUploads, removeLocalUpload, readEditorSave,
    fetchAccountDesigns, fetchAccountDesignData, deleteAccountDesign,
    saveGlobalDocDesign, openDesignInEditor, SavedDesignRow
} from '../utils/globalDocDesigns';

interface DocumentTemplateGalleryProps {
    onSelectTemplate?: (template: GlobalDocTemplateMeta) => void;
    theme?: 'light' | 'dark';
}

const CATEGORY_ICON = (category: string): string =>
    category === 'passport' ? '🛂' : category === 'drivers_license' ? '🪪' : category === 'national_id' ? '🆔' : '📋';

export const DocumentTemplateGallery: React.FC<DocumentTemplateGalleryProps> = ({
    onSelectTemplate,
    theme = 'light'
}) => {
    const [selectedCategory, setSelectedCategory] = useState<string>('all');
    const [selectedCountry, setSelectedCountry] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState<'browse' | 'upload' | 'my_templates'>('browse');
    const [uploadFile, setUploadFile] = useState<File | null>(null);
    const [uploadMetadata, setUploadMetadata] = useState({ name: '', category: 'passport', country: '', description: '' });
    const [uploadBusy, setUploadBusy] = useState(false);
    const [uploadNote, setUploadNote] = useState<string | null>(null);
    const [myRows, setMyRows] = useState<SavedDesignRow[]>([]);
    const [myNote, setMyNote] = useState<string | null>(null);
    const [refreshKey, setRefreshKey] = useState(0);
    const cloningEngine = useRef(new UniversalDocumentCloningEngine());

    const colors = {
        bg: theme === 'dark' ? '#111827' : '#F9FAFB',
        card: theme === 'dark' ? '#1F2937' : '#FFFFFF',
        text: theme === 'dark' ? '#FFFFFF' : '#111827',
        textSecondary: theme === 'dark' ? '#9CA3AF' : '#6B7280',
        border: theme === 'dark' ? '#374151' : '#E5E7EB',
        primary: '#3B82F6',
        success: '#10B981'
    };

    const countries = useMemo(() => {
        const seen = new Map<string, { code: string; name: string; flag: string }>();
        for (const t of GLOBAL_DOC_TEMPLATE_LIST) {
            if (!seen.has(t.country)) seen.set(t.country, { code: t.countryCode, name: t.country, flag: t.flag });
        }
        return Array.from(seen.values());
    }, []);

    const filteredTemplates = useMemo(() => {
        return GLOBAL_DOC_TEMPLATE_LIST.filter(t => {
            if (selectedCategory !== 'all' && t.category !== selectedCategory) return false;
            if (selectedCountry !== 'all' && t.countryCode !== selectedCountry) return false;
            if (searchQuery && !t.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
            return true;
        });
    }, [selectedCategory, selectedCountry, searchQuery]);

    const openTemplate = useCallback((template: GlobalDocTemplateMeta) => {
        if (onSelectTemplate) { onSelectTemplate(template); return; }
        window.location.assign(`/document-editor?template=${encodeURIComponent(template.id)}`);
    }, [onSelectTemplate]);

    // ---- My Templates data ----
    const refreshMine = useCallback(async () => {
        const rows: SavedDesignRow[] = [];
        const editorSave = readEditorSave();
        if (editorSave) rows.push({ id: 'editor-save', name: editorSave.name, at: editorSave.at, source: 'editor-save', state: editorSave.state });
        for (const u of readLocalUploads()) {
            rows.push({ id: u.id, name: u.name, at: u.at, source: 'upload', state: u.state });
        }
        const { designs } = await fetchAccountDesigns();
        for (const d of designs) {
            rows.push({ id: d.id, name: d.name, at: d.updated_at ? new Date(d.updated_at).getTime() : 0, source: 'account' });
        }
        rows.sort((a, b) => b.at - a.at);
        setMyRows(rows);
    }, []);

    useEffect(() => { if (activeTab === 'my_templates') { refreshMine(); } }, [activeTab, refreshKey, refreshMine]);

    const handleUpload = useCallback(async () => {
        if (!uploadFile || !uploadMetadata.name.trim() || !uploadMetadata.country || uploadBusy) return;
        setUploadBusy(true);
        setUploadNote(null);
        try {
            const cloned = await cloningEngine.current.cloneDocument(uploadFile);
            const doc = mapClonedDocument(cloned);
            doc.category = uploadMetadata.category;
            doc.country = uploadMetadata.country;
            const snapshot = {
                doc,
                borderConfig: { ...DEFAULT_DOC_BORDER },
                activeTemplate: doc.id,
                ...(uploadMetadata.description.trim() ? { description: uploadMetadata.description.trim() } : {})
            };
            const outcome = await saveGlobalDocDesign(uploadMetadata.name.trim(), snapshot);
            setUploadFile(null);
            setUploadMetadata({ name: '', category: 'passport', country: '', description: '' });
            setRefreshKey(k => k + 1);
            if (outcome.where === 'account') {
                setUploadNote('Saved to your account — find it under My Templates and open it in the editor.');
            } else if (outcome.reason === 'signed-out') {
                setUploadNote('Saved in this browser — find it under My Templates. Sign in to also keep uploads in your account.');
            } else if (outcome.reason === 'auth-failed') {
                setUploadNote("You're signed out — the design was saved in this browser instead. Find it under My Templates.");
            } else if (outcome.reason === 'too-large') {
                setUploadNote('Too large for account storage, so it was saved in this browser — find it under My Templates.');
            } else if (outcome.reason === 'quota') {
                setUploadNote('Save failed — browser storage is full or unavailable, and the design could not be kept. Free up space and try again.');
            } else {
                setUploadNote('The account save did not go through, so the design was saved in this browser — find it under My Templates.');
            }
        } catch (err) {
            console.error('Template upload failed:', err);
            setUploadNote('That file could not be turned into a template — nothing was saved. Please use a clear image of the document.');
        } finally {
            setUploadBusy(false);
        }
    }, [uploadFile, uploadMetadata, uploadBusy]);

    const openRow = useCallback(async (row: SavedDesignRow) => {
        if (row.source === 'account') {
            const data = await fetchAccountDesignData(row.id);
            if (!data) { setMyNote('Could not load that design from your account.'); return; }
            openDesignInEditor(data, row.name);
            return;
        }
        if (row.state) openDesignInEditor(row.state, row.name);
    }, []);

    const deleteRow = useCallback(async (row: SavedDesignRow) => {
        if (row.source === 'upload') {
            removeLocalUpload(row.id);
            setRefreshKey(k => k + 1);
        } else if (row.source === 'account') {
            const ok = await deleteAccountDesign(row.id);
            setMyNote(ok ? 'Deleted from your account.' : 'Could not delete that design.');
            setRefreshKey(k => k + 1);
        }
    }, []);

    const when = (at: number) => at
        ? `${new Date(at).toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : '';

    return (
        <div className="oc-card-page" style={{ background: colors.bg, borderRadius: '16px', padding: '24px', minHeight: '600px' }}>
            <div style={{ marginBottom: '24px' }}>
                <h2 style={{ margin: '0 0 4px', color: colors.text, fontSize: '22px', fontWeight: '700' }}>
                    🌍 Global Document Templates
                </h2>
                <p style={{ margin: 0, color: colors.textSecondary, fontSize: '14px' }}>
                    Browse {GLOBAL_DOC_TEMPLATE_LIST.length} templates from {countries.length} countries — every one opens in the editor
                </p>
            </div>

            {/* Tabs */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                {(['browse', 'upload', 'my_templates'] as const).map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{
                        padding: '10px 20px',
                        background: activeTab === tab ? colors.primary : 'transparent',
                        color: activeTab === tab ? '#FFF' : colors.textSecondary,
                        border: `1px solid ${activeTab === tab ? colors.primary : colors.border}`,
                        borderRadius: '8px', cursor: 'pointer', fontWeight: '500', textTransform: 'capitalize'
                    }}>
                        {tab === 'browse' && '🔍 '}{tab === 'upload' && '📤 '}{tab === 'my_templates' && '📁 '}
                        {tab.replace('_', ' ')}
                    </button>
                ))}
            </div>

            {activeTab === 'browse' && (
                <>
                    {/* Filters */}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        <select value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}
                            style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.card, color: colors.text, minWidth: '180px' }}>
                            <option value="all">All Categories</option>
                            {GLOBAL_DOC_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                        </select>
                        <select value={selectedCountry} onChange={e => setSelectedCountry(e.target.value)}
                            style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.card, color: colors.text, minWidth: '180px' }}>
                            <option value="all">All Countries</option>
                            {countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                        </select>
                        <input type="text" placeholder="Search templates..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                            style={{ padding: '10px 16px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.card, color: colors.text, flex: 1, minWidth: '200px' }} />
                    </div>

                    {/* Category Quick Select */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
                        {GLOBAL_DOC_CATEGORIES.map(c => (
                            <button key={c.id} onClick={() => setSelectedCategory(c.id)}
                                style={{
                                    padding: '8px 16px', borderRadius: '20px', cursor: 'pointer',
                                    background: selectedCategory === c.id ? colors.primary : colors.card,
                                    color: selectedCategory === c.id ? '#FFF' : colors.text,
                                    border: `1px solid ${selectedCategory === c.id ? colors.primary : colors.border}`,
                                    fontSize: '13px', fontWeight: '500'
                                }}>
                                {c.icon} {c.name}
                            </button>
                        ))}
                    </div>

                    {/* Results count */}
                    <div style={{ marginBottom: '16px', color: colors.textSecondary, fontSize: '13px' }}>
                        Showing {filteredTemplates.length} templates
                    </div>

                    {/* Template Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
                        {filteredTemplates.map(template => (
                            <div key={template.id} data-template-card={template.id} onClick={() => openTemplate(template)}
                                style={{
                                    background: colors.card, borderRadius: '12px', padding: '16px',
                                    border: `1px solid ${colors.border}`, cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s'
                                }}
                                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)'; }}
                                onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                                    <div style={{ fontSize: '32px' }}>{CATEGORY_ICON(template.category)}</div>
                                </div>
                                <h3 style={{ margin: '0 0 4px', color: colors.text, fontSize: '15px', fontWeight: '600' }}>{template.name}</h3>
                                <p style={{ margin: '0 0 12px', color: colors.textSecondary, fontSize: '12px' }}>
                                    {template.flag} {template.country}
                                    {template.region && ` • ${template.region}`}
                                </p>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <span style={{ color: colors.primary, fontSize: '12px', fontWeight: '600' }}>
                                        Open in Editor →
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    {filteredTemplates.length === 0 && (
                        <div style={{ textAlign: 'center', padding: '40px 0', color: colors.textSecondary }}>
                            <p>No templates match that filter.</p>
                        </div>
                    )}
                </>
            )}

            {activeTab === 'upload' && (
                <div style={{ background: colors.card, borderRadius: '12px', padding: '24px', border: `1px solid ${colors.border}` }}>
                    <h3 style={{ margin: '0 0 8px', color: colors.text }}>📤 Upload &amp; Save as My Template</h3>
                    <p style={{ margin: '0 0 20px', color: colors.textSecondary, fontSize: '13px' }}>
                        The document is turned into an editable design and saved — to your account when you are signed in, otherwise in this browser. It appears under My Templates, ready to open in the editor.
                    </p>

                    {/* File Upload */}
                    <div onClick={() => document.getElementById('template-file-input')?.click()}
                        style={{ border: `2px dashed ${colors.border}`, borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer', marginBottom: '20px' }}>
                        <input id="template-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                            onChange={e => setUploadFile(e.target.files?.[0] || null)} />
                        <div style={{ fontSize: '40px', marginBottom: '12px' }}>{uploadFile ? '✅' : '📄'}</div>
                        <div style={{ color: colors.text, fontWeight: '500' }}>{uploadFile ? uploadFile.name : 'Drop document here or click to upload'}</div>
                        <div style={{ color: colors.textSecondary, fontSize: '12px', marginTop: '8px' }}>Upload any ID, passport, or document to create a reusable template</div>
                    </div>

                    {/* Metadata Form */}
                    <div className="oc-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                        <div>
                            <label style={{ display: 'block', color: colors.text, fontSize: '13px', marginBottom: '6px' }}>Template Name *</label>
                            <input type="text" value={uploadMetadata.name} onChange={e => setUploadMetadata({ ...uploadMetadata, name: e.target.value })}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text }} />
                        </div>
                        <div>
                            <label style={{ display: 'block', color: colors.text, fontSize: '13px', marginBottom: '6px' }}>Category *</label>
                            <select value={uploadMetadata.category} onChange={e => setUploadMetadata({ ...uploadMetadata, category: e.target.value })}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text }}>
                                {GLOBAL_DOC_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={{ display: 'block', color: colors.text, fontSize: '13px', marginBottom: '6px' }}>Country *</label>
                            <input type="text" placeholder="e.g. United States" value={uploadMetadata.country} onChange={e => setUploadMetadata({ ...uploadMetadata, country: e.target.value })}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text }} />
                        </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', color: colors.text, fontSize: '13px', marginBottom: '6px' }}>Description</label>
                        <textarea value={uploadMetadata.description} onChange={e => setUploadMetadata({ ...uploadMetadata, description: e.target.value })} rows={3}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, resize: 'vertical' }} />
                    </div>

                    <button onClick={handleUpload} disabled={!uploadFile || !uploadMetadata.name.trim() || !uploadMetadata.country || uploadBusy}
                        style={{
                            width: '100%', padding: '14px', borderRadius: '8px', border: 'none', fontSize: '15px', fontWeight: '600', cursor: 'pointer',
                            background: uploadFile && uploadMetadata.name.trim() && uploadMetadata.country && !uploadBusy ? colors.primary : colors.border,
                            color: '#FFF'
                        }}>
                        {uploadBusy ? '⏳ Saving…' : '📤 Upload Template'}
                    </button>
                    {uploadNote && (
                        <div style={{ marginTop: '14px', padding: '10px 12px', borderRadius: '8px', background: `${colors.border}50`, color: colors.text, fontSize: '13px' }}>{uploadNote}</div>
                    )}
                </div>
            )}

            {activeTab === 'my_templates' && (
                <div>
                    {myNote && (
                        <div style={{ marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', background: `${colors.border}50`, color: colors.text, fontSize: '13px' }}>{myNote}</div>
                    )}
                    {myRows.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: colors.textSecondary }}>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📁</div>
                            <h3 style={{ color: colors.text, marginBottom: '8px' }}>Your Templates</h3>
                            <p>Nothing saved yet. Save a design in the Document Editor or upload a document, and it will appear here.</p>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {myRows.map(row => (
                                <div key={`${row.source}-${row.id}`} data-my-template={row.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '10px', padding: '10px 14px' }}>
                                    <span style={{ fontSize: '13px', color: colors.text, flex: '1 1 auto', minWidth: 0 }}>
                                        {row.source === 'account' ? '☁️' : '💾'} <strong>{row.name}</strong>{' '}
                                        <span style={{ color: colors.textSecondary, fontSize: '11px' }}>
                                            {row.source === 'account' ? 'In your account' : row.source === 'upload' ? 'Uploaded · in this browser' : 'Saved in this browser'}
                                            {row.at ? ` · ${when(row.at)}` : ''}
                                            {row.state?.description ? ` · ${row.state.description}` : ''}
                                        </span>
                                    </span>
                                    <button type="button" onClick={() => openRow(row)}
                                        style={{ padding: '6px 14px', borderRadius: '8px', border: 'none', background: colors.primary, color: '#FFF', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                                        Open in Editor
                                    </button>
                                    {row.source !== 'editor-save' && (
                                        <button type="button" onClick={() => deleteRow(row)} title="Delete this saved design"
                                            style={{ padding: '6px 10px', borderRadius: '8px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, fontSize: '12px', cursor: 'pointer' }}>
                                            🗑
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <p style={{ color: colors.textSecondary, fontSize: '12px', marginTop: '14px' }}>Sign in to also keep these designs in your account, on any device.</p>
                </div>
            )}
        </div>
    );
};

export default DocumentTemplateGallery;
